import React from "react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { contact, keyFeatures, hero, gallery } from "../mock";
import { CheckCircle2, MessageCircle, Trophy, Zap, Wallet, ShieldCheck } from "lucide-react";
import CmsContent from "../components/CmsContent";
import WhatsAppImage from "../components/WhatsAppImage";

const points = [
  { icon: Trophy, title: "All Matches Covered", desc: "IPL, World Cup, BBL, PSL, T20 and every international & domestic league with live odds." },
  { icon: Zap, title: "2-Minute Activation", desc: "No forms, no document uploads. Just message on WhatsApp and get your id instantly." },
  { icon: Wallet, title: "Deposit from ₹100", desc: "Add balance via UPI, Bank or USDT and withdraw your winnings in 3–5 minutes." },
  { icon: ShieldCheck, title: "100% Safe & Secure", desc: "SSL-encrypted platform with complete privacy. Your data is never shared with anyone." },
];

const CricketId = () => {
  return (
    <main>
      <PageHero
        crumb="Cricket ID"
        title="Online"
        highlight="Cricket ID"
        subtitle="Your verified online cricket id from DaddyAnna — 100% safe, fast and easy to use. Play every match live with real-time odds."
      />

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <div>
                <h2 className="font-display text-3xl font-bold text-white mb-5">
                  What is a <span className="neon-green text-glow-green">Cricket ID</span>?
                </h2>
                <p className="text-gray-400 leading-relaxed mb-4">
                  A cricket id is your personal gateway to India's top sports exchange platforms. With a single verified id, you can follow live matches, view real-time odds and enjoy the full thrill of cricket right from your phone.
                </p>
                <p className="text-gray-400 leading-relaxed mb-6">
                  At DaddyAnna, getting an online cricket id is simple and reliable. Operating since 2015, we are trusted by 50,000+ players for instant IDs, transparent dealings and lightning-fast withdrawals.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {keyFeatures.map((f) => (
                    <div key={f} className="flex items-center gap-2.5 text-gray-200 text-sm">
                      <CheckCircle2 size={18} className="text-[#00ff87] shrink-0" /> {f}
                    </div>
                  ))}
                </div>
                <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="btn-neon mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-display font-semibold">
                  <MessageCircle size={20} /> Get Cricket ID Now
                </a>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#00ff87]/20 to-[#00e5ff]/20 blur-3xl rounded-full" />
                <div className="neon-ring absolute -inset-4 z-0 hidden md:block" />
                <WhatsAppImage src={hero.image} alt="Online cricket id" rounded="rounded-2xl" className="relative z-10" imgClassName="aspect-[3/2]" label="Get Cricket ID on WhatsApp" />
                <div className="neon-ball absolute -bottom-5 -right-5 h-16 w-16 z-20 animate-floaty" style={{ animationDelay: "1s" }} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#08080f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Benefits" title="Why Get Your ID from" highlight="DaddyAnna" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {points.map((p, i) => (
              <Reveal key={p.title} delay={i * 90}>
                <div className="card-neon rounded-2xl p-6 h-full">
                  <div className="h-12 w-12 grid place-items-center rounded-xl bg-[#00ff87]/10 text-[#00ff87] mb-4">
                    <p.icon size={24} />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-white mb-2">{p.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
            {gallery.map((g, i) => (
              <WhatsAppImage key={g} src={g} alt={`cricket id ${i + 1}`} rounded="rounded-xl" className="aspect-square" />
            ))}
          </div>
        </div>
      </section>
      <CmsContent slug="cricket-id" fallbackTitle="Online Cricket ID | DaddyAnna" />
    </main>
  );
};

export default CricketId;
